import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import { TagMemberType } from '@design-system/components/molecules/tag-member/entities/tag-member.types';
import { RatesSignalTypes } from '@autos/auto-results/components/auto-detail-rates/constants/rates';
import {
  RatesTypes, RateUI
} from '@autos/auto-results/components/auto-detail-rates/entities/rates';
import { environment as ENV } from '@environment';
import {
  PriceDetailCardDiscount, PriceDetailCardPrice
} from '@design-system/components/organisms/price-detail-card/entities/price-detail-card.types';
import { CurrencyFormatPipe } from '@app/pipes/currency-format.pipe';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { PriceDetailService } from '@shared/payment-detail/services/payment-detail.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-auto-detail-rates',
  templateUrl: './auto-detail-rates.component.html',
  styleUrls: ['./auto-detail-rates.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AutoDetailRatesComponent {

  @Input() period: string;
  @Input() currency: string;
  @Input() rates: RateUI[] = [];
  @Input() conversionRate: number;
  @Input() isUserHistory = false;

  public activateTooltip: boolean = false;

  public readonly tagMemberType = TagMemberType;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly transloco: TranslocoService,
    private readonly currencyFormatPipe: CurrencyFormatPipe,
    private readonly service: PriceDetailService,
  ) { }

  get discount$(): Observable<PriceDetailCardDiscount> {
    const rate = this.rates?.find(_rate => _rate.id === RatesTypes.DiscountAmount);
    return rate?.value && rate?.percentage ? 
      this.service.getCardDiscount$(SearchBy.AUTOS, rate?.value, rate?.percentage, this.currency) : of(null);
  }

  get rows(): PriceDetailCardPrice[][] {
    return [
      this.rates?.filter(
        rate => rate.id !== RatesTypes.DiscountAmount && !this.isTotalPay(rate)
      ).map(rate => ({
        Label: this.transloco.translate(rate.label),
        Amount: `${this.signal(rate.signal)}${this.currencyFormatPipe.transform(rate.value, this.currency)}`,
        Info: rate.id === RatesTypes.PaymentAgency ? this.transloco.translate('AUTOS_MODULE.RATES.TOOLTIP') : null
      }))
    ];
  }

  get total(): number {
    const rate = this.rates?.find(_rate => this.isTotalPay(_rate));
    return rate ? rate.value : null;
  }

  get applyConversion(): boolean {
    return !!ENV.countryModules?.booking?.exchange_rate?.enabled && !!this.conversionRate;
  }

  get currencyExchangeName(): string {
    return ENV.countryModules?.booking?.exchange_rate?.currency_to_name;
  }

  public isTotalPay(rate: RateUI): boolean {
    return RatesTypes.Total === rate.id || RatesTypes.TotalPay  === rate.id;
  }

  public withReference(rate: RateUI): boolean {
    return this.isTotalPay(rate) && this.applyConversion;
  }

  public signal(s: RatesSignalTypes): string {
    return (s === RatesSignalTypes.SUB) ? '-' : (s === RatesSignalTypes.MULTIPLY) ? '*' : '';
  }

  public toogleTooltip(): void {
    this.activateTooltip = !this.activateTooltip;
  }
}
